import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import { AlertCircle } from "lucide-react";
import {
	transformSimpleBarData,
	transformStackedBarData
} from '../../utils/chartDataTransformer';
import { useApp } from '../../contexts/AppProvider';

function MultiBarCharts({ chartData }) {
	const { language } = useApp();

	// Helper function to check if data fetch failed
	const hasFailed = (data) => {
		return data && data.has_failed === true;
	};

	const chartConfigs = [
		{
			title: language === "en" ? "Responses by status distribution" : "Distribution des réponses par status",
			type: "simple",
			data: hasFailed(chartData.number_of_response_by_status)
				? null
				: transformSimpleBarData(
					chartData.number_of_response_by_status,
					"status",
					"count"
				),
			failedData: chartData.number_of_response_by_status,
		},
		{
			title: language === "en" ? "Responses by token and status" : "Distribution des réponses par token et par status",
			type: "stacked",
			data: hasFailed(chartData.response_by_token_name_and_status)
				? null
				: transformStackedBarData(
					chartData.response_by_token_name_and_status,
					"token_name",
					"status",
					"response_count"
				),
			failedData: chartData.response_by_token_name_and_status,
		},
	];
	
	return (
		<div className="flex flex-col gap-8">
			{chartConfigs.map((chart, i) => {
				const isFailed = hasFailed(chart.failedData);
				const allKeys = chart.type === "stacked" && chart.data
					? Object.keys(chart.data[0] || {}).filter((k) => k !== "token_name")
					: [];
	
				return (
					<div
						key={i}
						className="p-6 border shadow-sm bg-[var(--color-surface)]"
						style={{
							borderColor: "color-mix(in srgb, var(--color-muted) 50%, transparent)",
						}}
					>
						<h3
							className="text-lg font-semibold mb-4"
							style={{ color: "var(--color-primary)" }}
						>
							{chart.title}
						</h3>
	
						<div style={{ width: "100%", height: 300 }}>
							{isFailed ? (
								<div className="w-full h-full flex flex-col items-center justify-center gap-3">
									<AlertCircle 
										size={32} 
										style={{ color: "var(--color-muted)" }}
									/>
									<p 
										className="text-sm text-center"
										style={{ color: "var(--color-muted)" }}
									>
										{language === "en" 
											? "Data temporarily unavailable" 
											: "Données temporairement indisponibles"}
									</p>
								</div>
							) : (
								<ResponsiveContainer>
									{chart.type === "simple" ? (
										<BarChart data={chart.data}>
											<CartesianGrid
												stroke="color-mix(in srgb, var(--color-muted) 50%, transparent)"
												strokeDasharray="3 3"
											/>
											<XAxis dataKey="name" stroke="var(--color-text)" />
											<YAxis stroke="var(--color-text)" />
											<Tooltip />
											<Legend />
											<Bar
												dataKey="value"
												fill="var(--color-primary)"
												radius={[6, 6, 0, 0]}
											/>
										</BarChart>
									) : (
										<BarChart data={chart.data}>
											<CartesianGrid
												stroke="color-mix(in srgb, var(--color-muted) 50%, transparent)"
												strokeDasharray="3 3"
											/>
											<XAxis dataKey="token_name" stroke="var(--color-text)" />
											<YAxis stroke="var(--color-text)" />
											<Tooltip />
											<Legend />
											{allKeys.map((key, index) => {
												const color = index === 0
													? "var(--color-primary)"
													: `hsl(${(index * 60) % 360}, 70%, 60%)`;
	
												return (
													<Bar
														key={key}
														dataKey={key}
														stackId="a"
														fill={color}
														radius={[6, 6, 0, 0]}
													/>
												);
											})}
										</BarChart>
									)}
								</ResponsiveContainer>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
	
export default MultiBarCharts;